import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsIcon from "@mui/icons-material/Collections";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import orgDataService from "services/orgDataService";
import vehiclesService from "services/vehiclesService";
import employeeService from "services/employeeService";
import formsService from "services/formsService";
import { Hub } from "common/models";
import { Employee } from "employee/models";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Moment } from "moment";

type ActionItem = "outsideHosPurchase" | "resetCard" | "replaceCard" | null;

interface VehicleOption {
  id: number;
  unitNumber: number;
}

interface FormData {
  actionItem: ActionItem;
  vehicleLocationId: number | null;
  employeeId: number | null;
  // Outside HOS Purchase fields
  unitNumber: number | null;
  tripShiftNumber: string;
  reasonForPurchase: string;
  purchaseDateTime: Moment | null;
  purchaseAmount: string;
  receiptPictures: File[];
  // Reset Card fields
  reasonForReset: string;
  // Replace Card fields
  replaceReason: string;
}

interface FormErrors {
  actionItem?: string;
  vehicleLocationId?: string;
  employeeId?: string;
  unitNumber?: string;
  tripShiftNumber?: string;
  reasonForPurchase?: string;
  purchaseDateTime?: string;
  purchaseAmount?: string;
  receiptPictures?: string;
  reasonForReset?: string;
  replaceReason?: string;
}

const actionItemOptions = [
  { value: "outsideHosPurchase", label: "Outside HOS Purchase" },
  { value: "resetCard", label: "Reset Card" },
  { value: "replaceCard", label: "Replace Card" },
];

const purchaseReasonOptions = [
  "Low Fuel",
  "Emergency",
  "Long Distance Trip",
  "Other",
];

const resetReasonOptions = [
  "Card Locked",
  "PIN Reset Required",
  "Card Malfunction",
  "Other",
];

const replaceReasonOptions = [
  "Lost Card",
  "Stolen Card",
  "Damaged Card",
  "Expired Card",
  "Other",
];

const FuelCard = () => {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [locations, setLocations] = useState<Hub[]>([]);
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<FormData>({
    actionItem: null,
    vehicleLocationId: null,
    employeeId: null,
    unitNumber: null,
    tripShiftNumber: "",
    reasonForPurchase: "",
    purchaseDateTime: null,
    purchaseAmount: "",
    receiptPictures: [],
    reasonForReset: "",
    replaceReason: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.vehicleLocationId) {
      loadVehiclesByHub(formData.vehicleLocationId);
      loadEmployeesByHub(formData.vehicleLocationId);
    } else {
      setVehicles([]);
      setEmployees([]);
    }
  }, [formData.vehicleLocationId]);

  const loadData = async () => {
    try {
      const hubsData = await orgDataService.getAllHubs();
      setLocations(hubsData);
    } catch (error) {
      toast.error("Failed to load form data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadVehiclesByHub = async (hubId: number) => {
    setIsLoadingVehicles(true);
    try {
      const vehiclesData = await vehiclesService.getVehiclesByHub(hubId);
      setVehicles(
        vehiclesData.map((v: any) => ({
          id: v.id,
          unitNumber: v.unitNumber,
        }))
      );
    } catch (error) {
      toast.error("Failed to load vehicles");
      setVehicles([]);
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  const loadEmployeesByHub = async (hubId: number) => {
    setIsLoadingEmployees(true);
    try {
      const result = await employeeService.searchEmployees({
        hubId: hubId,
        status: "activeOnly",
        pagedSearchOptions: { offset: 0, pageSize: 500 },
        sortName: "lastName",
        sortAsc: true,
      } as any);
      setEmployees(result.results || []);
    } catch (error) {
      toast.error("Failed to load employees");
      setEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const getValidationSchema = () => {
    const baseSchema: any = {
      actionItem: Yup.string().required("Action item is required").nullable(),
      vehicleLocationId: Yup.number().required("Location is required").nullable(),
      employeeId: Yup.number().required("Employee is required").nullable(),
    };

    if (formData.actionItem === "outsideHosPurchase") {
      baseSchema.unitNumber = Yup.number().required("Vehicle is required").nullable();
      baseSchema.tripShiftNumber = Yup.string().required("Trip/Shift # is required");
      baseSchema.reasonForPurchase = Yup.string().required("Reason for purchase is required");
      baseSchema.purchaseDateTime = Yup.date().required("Date & time is required").nullable();
      baseSchema.purchaseAmount = Yup.string().required("Amount is required");
    } else if (formData.actionItem === "resetCard") {
      baseSchema.unitNumber = Yup.number().required("Vehicle is required").nullable();
      baseSchema.reasonForReset = Yup.string().required("Reason for reset is required");
      baseSchema.tripShiftNumber = Yup.string().required("Trip/Shift # is required");
    } else if (formData.actionItem === "replaceCard") {
      baseSchema.replaceReason = Yup.string().required("Replace reason is required");
    }

    return Yup.object().shape(baseSchema);
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    try {
      getValidationSchema().validateSync(formData, { abortEarly: false });
    } catch (error) {
      const validation = error as Yup.ValidationError;
      validation.inner.forEach((err) => {
        if (err.path) {
          newErrors[err.path as keyof FormErrors] = err.message;
        }
      });
    }

    if (formData.actionItem === "outsideHosPurchase" && formData.receiptPictures.length === 0) {
      newErrors.receiptPictures = "At least one receipt picture is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const selectedLocation = locations.find((l) => l.id === formData.vehicleLocationId);
    const selectedEmployee = employees.find((e) => e.id === formData.employeeId);

    if (!selectedLocation || !selectedEmployee) {
      toast.error("Please select a valid location and employee");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await formsService.submitFuelCard(
        {
          actionItem: formData.actionItem || "",
          vehicleLocationId: formData.vehicleLocationId,
          vehicleLocationName: selectedLocation.name,
          unitNumber: formData.unitNumber,
          tripShiftNumber: formData.tripShiftNumber,
          reasonForReset: formData.reasonForReset,
          reasonForPurchase: formData.reasonForPurchase,
          purchaseDateTime: formData.purchaseDateTime
            ? formData.purchaseDateTime.format("YYYY-MM-DDTHH:mm:ss")
            : "",
          purchaseAmount: formData.purchaseAmount,
          replaceReason: formData.replaceReason,
          employeeName: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
          employeeNumber: selectedEmployee.employeeNumber,
        },
        formData.actionItem === "outsideHosPurchase" ? formData.receiptPictures : []
      );

      if (response.success) {
        toast.success(response.message || "Fuel card request submitted");
        navigate("/forms");
      } else {
        toast.error(response.message || "Failed to submit fuel card request");
      }
    } catch (error) {
      toast.error("Failed to submit fuel card request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    if (field === "vehicleLocationId") {
      setFormData({ ...formData, [field]: value, unitNumber: null, employeeId: null });
      setErrors({
        ...errors,
        vehicleLocationId: undefined,
        unitNumber: undefined,
        employeeId: undefined,
      });
    } else if (field === "actionItem") {
      // Reset action-specific fields when changing action item; keep location/employee
      setFormData({
        ...formData,
        actionItem: value,
        unitNumber: null,
        tripShiftNumber: "",
        reasonForPurchase: "",
        purchaseDateTime: null,
        purchaseAmount: "",
        receiptPictures: [],
        reasonForReset: "",
        replaceReason: "",
      });
      setErrors({ ...errors, actionItem: undefined, receiptPictures: undefined });
    } else {
      setFormData({ ...formData, [field]: value });
      if (errors[field as keyof FormErrors]) {
        setErrors({ ...errors, [field]: undefined });
      }
    }
  };

  const handleFilesSelected = (
    files: FileList | null,
    inputRef: React.RefObject<HTMLInputElement>
  ) => {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setFormData({ ...formData, receiptPictures: [...formData.receiptPictures, ...newFiles] });
    if (errors.receiptPictures) {
      setErrors({ ...errors, receiptPictures: undefined });
    }
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    const updated = formData.receiptPictures.filter((_, i) => i !== index);
    setFormData({ ...formData, receiptPictures: updated });
  };

  if (isLoading) {
    return (
      <MDBox p={3}>
        <MDTypography>Loading...</MDTypography>
      </MDBox>
    );
  }

  const showOutsideHosPurchaseFields = formData.actionItem === "outsideHosPurchase";
  const showResetCardFields = formData.actionItem === "resetCard";
  const showReplaceCardFields = formData.actionItem === "replaceCard";
  const showVehicleField = showOutsideHosPurchaseFields || showResetCardFields;
  const showLocationAndEmployee = !!formData.actionItem;

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="medium">
            Fuel Card
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Fuel card action request form
          </MDTypography>
        </MDBox>
        <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
          Back to Forms
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.actionItem}>
                <InputLabel id="action-label">Action Item *</InputLabel>
                <Select
                  labelId="action-label"
                  id="actionItem"
                  value={formData.actionItem || ""}
                  label="Action Item *"
                  onChange={(e) => handleChange("actionItem", e.target.value)}
                >
                  {actionItemOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.actionItem && <FormHelperText>{errors.actionItem}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>

          {/* Location and Employee - shown for all action items (used in email body) */}
          {showLocationAndEmployee && (
            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.vehicleLocationId}>
                  <InputLabel id="vehicle-location-label">Location *</InputLabel>
                  <Select
                    labelId="vehicle-location-label"
                    id="vehicleLocationId"
                    value={formData.vehicleLocationId || ""}
                    label="Location *"
                    onChange={(e) => handleChange("vehicleLocationId", e.target.value)}
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.vehicleLocationId && (
                    <FormHelperText>{errors.vehicleLocationId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={!!errors.employeeId}
                  disabled={!formData.vehicleLocationId || isLoadingEmployees}
                >
                  <InputLabel id="employee-label">
                    {isLoadingEmployees ? "Loading employees..." : "Employee *"}
                  </InputLabel>
                  <Select
                    labelId="employee-label"
                    id="employeeId"
                    value={formData.employeeId || ""}
                    label={isLoadingEmployees ? "Loading employees..." : "Employee *"}
                    onChange={(e) => handleChange("employeeId", e.target.value)}
                  >
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {employee.lastName}, {employee.firstName}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.employeeId && <FormHelperText>{errors.employeeId}</FormHelperText>}
                  {!formData.vehicleLocationId && !errors.employeeId && (
                    <FormHelperText>Select a location first</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Vehicle Number - shown for Outside HOS Purchase and Reset Card */}
          {showVehicleField && (
            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={!!errors.unitNumber}
                  disabled={!formData.vehicleLocationId || isLoadingVehicles}
                >
                  <InputLabel id="vehicle-label">
                    {isLoadingVehicles ? "Loading vehicles..." : "Vehicle Number *"}
                  </InputLabel>
                  <Select
                    labelId="vehicle-label"
                    id="unitNumber"
                    value={formData.unitNumber || ""}
                    label={isLoadingVehicles ? "Loading vehicles..." : "Vehicle Number *"}
                    onChange={(e) => handleChange("unitNumber", e.target.value)}
                  >
                    {vehicles.map((vehicle) => (
                      <MenuItem key={vehicle.unitNumber} value={vehicle.unitNumber}>
                        {vehicle.unitNumber}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.unitNumber && <FormHelperText>{errors.unitNumber}</FormHelperText>}
                  {!formData.vehicleLocationId && !errors.unitNumber && (
                    <FormHelperText>Select a location first</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Outside HOS Purchase specific fields */}
          {showOutsideHosPurchaseFields && (
            <>
              <Grid container spacing={3} mt={0}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Trip / Shift # *"
                    value={formData.tripShiftNumber}
                    onChange={(e) => handleChange("tripShiftNumber", e.target.value)}
                    error={!!errors.tripShiftNumber}
                    helperText={errors.tripShiftNumber}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.reasonForPurchase}>
                    <InputLabel id="purchase-reason-label">Reason for Purchase *</InputLabel>
                    <Select
                      labelId="purchase-reason-label"
                      id="reasonForPurchase"
                      value={formData.reasonForPurchase}
                      label="Reason for Purchase *"
                      onChange={(e) => handleChange("reasonForPurchase", e.target.value)}
                    >
                      {purchaseReasonOptions.map((reason) => (
                        <MenuItem key={reason} value={reason}>
                          {reason}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.reasonForPurchase && (
                      <FormHelperText>{errors.reasonForPurchase}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={3} mt={0}>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Date & Time of Purchase *"
                    value={formData.purchaseDateTime}
                    onChange={(value) => handleChange("purchaseDateTime", value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.purchaseDateTime,
                        helperText: errors.purchaseDateTime,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Amount of Purchase *"
                    value={formData.purchaseAmount}
                    onChange={(e) => handleChange("purchaseAmount", e.target.value)}
                    error={!!errors.purchaseAmount}
                    helperText={errors.purchaseAmount}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mt={0}>
                <Grid item xs={12}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Receipt Pictures *
                  </MDTypography>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    onChange={(e) => handleFilesSelected(e.target.files, cameraInputRef)}
                  />
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleFilesSelected(e.target.files, galleryInputRef)}
                  />
                  <Stack direction="row" spacing={1}>
                    <MDButton
                      variant="outlined"
                      color="info"
                      startIcon={<PhotoCameraIcon />}
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      Take Photo
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="info"
                      startIcon={<CollectionsIcon />}
                      onClick={() => galleryInputRef.current?.click()}
                    >
                      Choose Photos
                    </MDButton>
                  </Stack>
                  {formData.receiptPictures.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={2} useFlexGap>
                      {formData.receiptPictures.map((file, index) => (
                        <Chip
                          key={`${file.name}-${index}`}
                          label={file.name}
                          onDelete={() => handleRemoveFile(index)}
                          deleteIcon={<CloseIcon />}
                        />
                      ))}
                    </Stack>
                  )}
                  {errors.receiptPictures && (
                    <FormHelperText error>{errors.receiptPictures}</FormHelperText>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          {/* Reset Card specific fields */}
          {showResetCardFields && (
            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.reasonForReset}>
                  <InputLabel id="reset-reason-label">Reason for Reset *</InputLabel>
                  <Select
                    labelId="reset-reason-label"
                    id="reasonForReset"
                    value={formData.reasonForReset}
                    label="Reason for Reset *"
                    onChange={(e) => handleChange("reasonForReset", e.target.value)}
                  >
                    {resetReasonOptions.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.reasonForReset && (
                    <FormHelperText>{errors.reasonForReset}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Trip / Shift # *"
                  value={formData.tripShiftNumber}
                  onChange={(e) => handleChange("tripShiftNumber", e.target.value)}
                  error={!!errors.tripShiftNumber}
                  helperText={errors.tripShiftNumber}
                />
              </Grid>
            </Grid>
          )}

          {/* Replace Card specific fields */}
          {showReplaceCardFields && (
            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.replaceReason}>
                  <InputLabel id="replace-reason-label">Replace Reason *</InputLabel>
                  <Select
                    labelId="replace-reason-label"
                    id="replaceReason"
                    value={formData.replaceReason}
                    label="Replace Reason *"
                    onChange={(e) => handleChange("replaceReason", e.target.value)}
                  >
                    {replaceReasonOptions.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.replaceReason && (
                    <FormHelperText>{errors.replaceReason}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
            <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              color="dark"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </MDButton>
          </Box>
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default FuelCard;
